import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { login as apiLogin, register as apiRegister, getMe as apiGetMe, setAuthTokens as apiSetAuthTokens } from '../services/api';

// Simple auth context to manage user and tokens on the frontend
// Backend wiring will be added next (JWT endpoints expected at /api/auth/*)

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  loading: true,
  login: async (email, password) => {},
  register: async (payload) => {},
  logout: () => {},
});

const STORAGE_KEY = 'triageos_auth';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Persist and hydrate from localStorage (basic for now)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.accessToken) {
          setAccessToken(saved.accessToken);
          setRefreshToken(saved.refreshToken || null);
          apiSetAuthTokens({ accessToken: saved.accessToken, refreshToken: saved.refreshToken });
        }
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  // When we have a token but no user, try to fetch current user profile
  useEffect(() => {
    let cancelled = false;
    const fetchMe = async () => {
      if (!accessToken || user) return;
      try {
        const me = await apiGetMe();
        if (!cancelled) setUser(me);
      } catch (e) {
        // token may be invalid; clear
        if (!cancelled) {
          handleLogout();
        }
      }
    };
    fetchMe();
    return () => { cancelled = true; };
  }, [accessToken]);

  const saveTokens = useCallback((at, rt) => {
    setAccessToken(at);
    setRefreshToken(rt || null);
    apiSetAuthTokens({ accessToken: at, refreshToken: rt });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken: at, refreshToken: rt || null }));
    } catch (e) {
      // ignore
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const resp = await apiLogin({ email, password });
    // Expecting { access: '...', refresh: '...' } shape (SimpleJWT)
    const at = resp?.access || resp?.access_token || null;
    const rt = resp?.refresh || resp?.refresh_token || null;
    if (!at) throw new Error('Login failed: missing access token');
    saveTokens(at, rt);
    const me = await apiGetMe();
    setUser(me);
    return me;
  }, [saveTokens]);

  const register = useCallback(async ({ first_name, last_name, email, phone, password }) => {
    await apiRegister({ first_name, last_name, email, phone, password });
    // Auto-login after registration (optional); for now, prompt sign-in separately.
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    apiSetAuthTokens({ accessToken: null, refreshToken: null });
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user && !!accessToken,
    accessToken,
    refreshToken,
    loading,
    login,
    register,
    logout: handleLogout,
  }), [user, accessToken, refreshToken, loading, login, register, handleLogout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
